import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import Calendar from '@/components/Calendar';
import { Avatar } from '@/components/Layout/Header';
import { SectionHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import { FreelancerVacationBalanceResource } from '@/types/resources';
import { api } from '@/utils/api';
import { FreelancerStatus, VacationRequestType, VacationType } from '@/utils/enums';
import { getInitialsFromString } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import VacationRequestModal from './VacationRequestModal';
import VacationTypeModal from './VacationTypeModal';

const Card = styled.div`
  gap: 16px;
  display: flex;
  flex-wrap: wrap;
  padding: 20px 24px;
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  background: ${({ theme }) => theme.colors.white};
  align-items: flex-start;
  margin-bottom: 16px;
`;

const StyledAvatar = styled(Avatar)`
  width: 56px;
  height: 56px;
  margin: 0;
`;

const AvatarHolder = styled.div`
  padding: 2px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 50%;
`;

const Info = styled.div`
  flex: 1;
`;

const UserInfo = styled.div`
  padding-bottom: 14px;
  padding-top: 5px;
  margin-bottom: 16px;
  border-bottom: solid 1px ${({ theme }) => theme.colors.lightGray};
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const VacationInfoHolder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const VacationInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Days = styled(Text)`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 58px;
`;

const Dot = styled.span<{ $lighter?: boolean }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.purple};
  ${({ $lighter, theme }) =>
    $lighter &&
    css`
      background: ${theme.colors.purpleLight};
    `}
`;

const StyledButton = styled(Button)`
  width: 100%;
  margin-bottom: 16px;
`;

const ResetInfo = styled(Text)`
  width: 100%;
  padding-top: 16px;
  border-top: solid 1px ${({ theme }) => theme.colors.lightGray};
  line-height: 20px;
  span {
    color: ${({ theme }) => theme.colors.black};
  }
`;

interface Props {
  isLoading: boolean;
  balance?: FreelancerVacationBalanceResource;
}

const FreelancerProfileCard: FC<Props> = ({ isLoading, balance }) => {
  const user = useSelector((state: RootState) => state.auth.user!);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [selectedType, setSelectedType] = useState<VacationRequestType | null>(null);
  const { data: roles } = useQuery<string[]>(
    [
      'freelancer_roles',
      { id: user?.freelancer?.id, status: FreelancerStatus.EMPLOYED },
    ],
    () =>
      api.get<never, string[]>(
        `/v1/payments/freelancers/${user?.freelancer?.id}/roles`,
        {
          params: {
            status: FreelancerStatus.EMPLOYED,
          },
        },
      ),
    {
      enabled: !!user?.freelancer?.id,
      staleTime: 60 * 1000,
      retry: 2,
    },
  );

  const remainingVacationDays = `${balance?.availableDays ?? '-'} ${balance?.availableDays === 1 ? 'day' : 'days'}`;
  const totalExtraDays =
    balance?.adjustments.reduce((sum, adj) => sum + adj.daysAdded, 0) ?? 0;

  return (
    <div>
      <SectionHeading>profile</SectionHeading>
      <Card>
        <AvatarHolder>
          {user.avatar ? (
            <StyledAvatar as="img" src={user.avatar} alt={user.name} />
          ) : (
            <StyledAvatar>
              <Text as="span" $type="smallBold">
                {getInitialsFromString(user.name)}
              </Text>
            </StyledAvatar>
          )}
        </AvatarHolder>
        <Info>
          <UserInfo>
            <Text $type="button">{balance?.freelancerName}</Text>
            <Text $type="smallText" $color="gray">
              {roles ? roles.join(', ') : '...'}
            </Text>
          </UserInfo>
          <VacationInfoHolder>
            <VacationInfo>
              <Text $type="smallText">Earned Vacation days:</Text>
              <Days $type="smallBold">
                <Dot $lighter />
                {isLoading
                  ? '...'
                  : `${balance?.allocatedDays} ${balance?.allocatedDays === 1 ? 'day' : 'days'}`}
              </Days>
            </VacationInfo>
            <VacationInfo>
              <Text $type="smallText">Extra days added:</Text>
              <Days $type="smallBold">
                <Dot $lighter />
                {isLoading
                  ? '...'
                  : `${totalExtraDays} ${totalExtraDays === 1 ? 'day' : 'days'}`}
              </Days>
            </VacationInfo>
            <VacationInfo>
              <Text $type="smallBold">Remaining vacation days:</Text>
              <Days $type="smallBold">
                <Dot />
                {isLoading ? '...' : remainingVacationDays}
              </Days>
            </VacationInfo>
          </VacationInfoHolder>
        </Info>
        {balance?.vacationType === VacationType.Instant && (
          <ResetInfo $type="smallText" $color="gray">
            Your vacation days (
            <span>
              {isLoading ? '...' : balance?.allocatedDaysAfterReset ?? '-'}
            </span>
            ) will reset at {isLoading ? '...' : balance?.resetDate ?? 0}. You
            can still request vacation for future dates.
          </ResetInfo>
        )}
      </Card>
      <StyledButton onClick={() => setShowTypeModal(true)}>
        New vacation request
      </StyledButton>
      <Calendar />
      <AnimatePresence>
        {showTypeModal && (
          <VacationTypeModal
            onSelect={(type) => {
              setSelectedType(type);
              setShowTypeModal(false);
            }}
            onClose={() => setShowTypeModal(false)}
          />
        )}
        {selectedType && (
          <VacationRequestModal
            onClose={() => setSelectedType(null)}
            balance={balance}
            type={selectedType}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FreelancerProfileCard;
